import type { StoryDirection, StoryEmote } from '@rdd/contracts'
import type { ApiClient } from '../api/client'
import {
  challengeClosedSchema,
  challengedSchema,
  challengePendingSchema,
  deltaSchema,
  duelReadySchema,
  emoteSchema,
  joinSchema,
  npcDuelClosedSchema,
  snapshotSchema,
  travelSchema,
  type ChallengeClosed,
  type Challenged,
  type ChallengePending,
  type Delta,
  type EmoteMessage,
  type Snapshot,
} from './protocol'

/**
 * A conexao do cliente com o mundo (modo_historia/04#HM-001). docs/05 secao 10, ADR 0043.
 *
 * Entrar e sempre o mesmo caminho: pedir a reserva ao `api` (`POST /story/world/join`) e entrar na sala com ela.
 * **Quem cai volta por esse mesmo caminho** -- a sala nao segura a vaga (docs/05 secao 10.1) --, ate
 * `MAX_REJOINS` vezes seguidas; depois disso, a tela mostra a falha com "tentar de novo".
 */

/** O pedaco do SDK do Colyseus que o mundo usa. Interface, para o teste trocar o transporte. */
export type WorldRoomLike = {
  onMessage(type: string, handler: (payload: unknown) => void): unknown
  onLeave(handler: (code: number) => void): unknown
  send(type: string, payload: unknown): void
  leave(consented?: boolean): unknown
}

export type WorldTransport = (
  endpoint: string,
  roomId: string,
  options: { userId: string; seatToken: string },
) => Promise<WorldRoomLike>

export type ConnectFailure = 'forbidden' | 'unavailable' | { refused: string }

export type WorldEvents = {
  onSnapshot(snapshot: Snapshot): void
  onDelta(delta: Delta): void
  onEmote(emote: EmoteMessage): void
  onChallenged(challenge: Challenged): void
  onChallengePending(challenge: ChallengePending): void
  onChallengeClosed(closed: ChallengeClosed): void
  onDuelReady(duelId: string): void
  /** O portal levou a outro mapa: a conexao ja saiu desta sala, e quem ouve refaz a entrada. */
  onTravel(mapId: string): void
  /** O duelo pedido ao NPC nao saiu, com o motivo. O que saiu chega por `onDuelReady`, como o do desafio. */
  onNpcDuelClosed(reason: string): void
  /** A conexao caiu e nao voltou. `reason` diz se foi o acesso (403) ou o resto. */
  onLost(reason: 'forbidden' | 'unavailable'): void
}

const MAX_REJOINS = 3
const REJOIN_DELAYS_MS = [300, 1000, 3000]

export class WorldConnection {
  private room: WorldRoomLike | null = null
  private leaving = false
  /** O duelo contra NPC recem-terminado, da ultima entrada (docs/21 secao 13). */
  npcOutcome: { botId: string; won: boolean } | null = null

  constructor(
    private readonly api: ApiClient,
    private readonly transport: WorldTransport,
    private readonly events: WorldEvents,
    private readonly sleep: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  ) {}

  /**
   * Entra no mundo. Devolve o que deu errado, ou `null` se entrou. `refused` e a exclusividade (docs/09 secao
   * 1.1): o jogador esta em outro modo, e o `code` diz qual.
   */
  async connect(): Promise<ConnectFailure | null> {
    const join = await this.api.request('/story/world/join', joinSchema, { method: 'POST', body: {} })
    if (!join.ok) {
      if (join.status === 403) return 'forbidden'
      if (join.status === 409) return { refused: join.code }
      return 'unavailable'
    }

    this.npcOutcome = join.data.npcOutcome
    let room: WorldRoomLike
    try {
      room = await this.transport(join.data.endpoint, join.data.roomId, {
        userId: join.data.userId,
        seatToken: join.data.seatToken,
      })
    } catch {
      return 'unavailable'
    }

    this.room = room
    room.onMessage('snapshot', (payload) => {
      const parsed = snapshotSchema.safeParse(payload)
      if (parsed.success) this.events.onSnapshot(parsed.data)
    })
    room.onMessage('delta', (payload) => {
      const parsed = deltaSchema.safeParse(payload)
      if (parsed.success) this.events.onDelta(parsed.data)
    })
    room.onMessage('emote', (payload) => {
      const parsed = emoteSchema.safeParse(payload)
      if (parsed.success) this.events.onEmote(parsed.data)
    })
    room.onMessage('challenged', (payload) => {
      const parsed = challengedSchema.safeParse(payload)
      if (parsed.success) this.events.onChallenged(parsed.data)
    })
    room.onMessage('challengePending', (payload) => {
      const parsed = challengePendingSchema.safeParse(payload)
      if (parsed.success) this.events.onChallengePending(parsed.data)
    })
    room.onMessage('challengeClosed', (payload) => {
      const parsed = challengeClosedSchema.safeParse(payload)
      if (parsed.success) this.events.onChallengeClosed(parsed.data)
    })
    room.onMessage('duelReady', (payload) => {
      const parsed = duelReadySchema.safeParse(payload)
      if (parsed.success) this.events.onDuelReady(parsed.data.duelId)
    })
    room.onMessage('npcDuelClosed', (payload) => {
      const parsed = npcDuelClosedSchema.safeParse(payload)
      if (parsed.success) this.events.onNpcDuelClosed(parsed.data.reason)
    })
    room.onMessage('travel', (payload) => {
      const parsed = travelSchema.safeParse(payload)
      if (!parsed.success) return
      this.leave()
      this.events.onTravel(parsed.data.mapId)
    })
    room.onLeave(() => {
      if (this.room === room) this.room = null
      if (!this.leaving) void this.rejoin()
    })
    return null
  }

  sendStep(dir: StoryDirection): void {
    this.room?.send('step', { dir })
  }

  sendEmote(id: StoryEmote): void {
    this.room?.send('emote', { id })
  }

  challenge(targetId: string): void {
    this.room?.send('challenge', { targetId })
  }

  /** Pede o duelo contra o NPC ao alcance (docs/21 secao 13). */
  npcDuel(npcId: string): void {
    this.room?.send('npcDuel', { npcId })
  }

  answer(challengeId: string, accept: boolean): void {
    this.room?.send('answer', { challengeId, accept })
  }

  /** Saida consentida: nao tenta voltar. */
  leave(): void {
    this.leaving = true
    void this.room?.leave(true)
    this.room = null
  }

  private async rejoin(): Promise<void> {
    for (let attempt = 0; attempt < MAX_REJOINS; attempt++) {
      await this.sleep(REJOIN_DELAYS_MS[attempt] ?? 3000)
      if (this.leaving) return
      const failure = await this.connect()
      if (failure === null) return
      if (failure !== 'unavailable') {
        this.events.onLost('forbidden')
        return
      }
    }
    this.events.onLost('unavailable')
  }
}
